import {Camera, ChevronLeft} from 'lucide-react';
import {forwardRef, type ReactNode, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {cn} from '../lib/cn';
import {getDocument} from '../lib/utils';
import {Webcam, type WebcamProps} from '../webcam';
import type {VideoConstraints, WebcamRefTypes} from '../webcam';
import {DeviceCamera} from './DeviceCamera';

const webcamClasses = 'fixed left-0 top-0 z-[6] h-full w-full rounded-none';

const scanAreaClasses = String.raw`[&_.webcam\_\_scan-area]:h-full [&_.webcam\_\_scan-area]:max-h-[209px] landscape:[&_.webcam\_\_scan-area]:h-[80%] landscape:[&_.webcam\_\_scan-area]:max-h-full md:[&_.webcam\_\_scan-area]:h-auto md:[&_.webcam\_\_scan-area]:max-h-none md:[&_.webcam\_\_scan-area]:aspect-[2/1]`;

const roundedWebcamClasses = String.raw`[&_.webcam\_\_scan-area]:top-[-60px] [&_.webcam\_\_scan-area]:h-full [&_.webcam\_\_scan-area]:w-full [&_.webcam\_\_scan-area]:max-h-[55%] [&_.webcam\_\_scan-area]:max-w-[80%] max-[375px]:[&_.webcam\_\_scan-area]:max-h-[369px] max-[375px]:[&_.webcam\_\_scan-area]:max-w-[261px] max-[320px]:[&_.webcam\_\_scan-area]:max-h-[318px] max-[320px]:[&_.webcam\_\_scan-area]:max-w-[220px] landscape:[&_.webcam\_\_scan-area]:h-full landscape:[&_.webcam\_\_scan-area]:w-full landscape:[&_.webcam\_\_scan-area]:max-h-[90%] landscape:[&_.webcam\_\_scan-area]:max-w-[261px] md:[&_.webcam\_\_scan-area]:aspect-square`;

function getAndMapFacingMode(constraints: VideoConstraints) {
  if (typeof constraints === 'boolean' || !constraints) return;
  if (constraints.facingMode === 'user') return 'user';
  if (constraints.facingMode === 'environment') return 'environment';
}

export type MobileScreenshotPayload =
  | {
      isNative: true;
      photo?: Promise<string>;
    }
  | {isNative: false};

export type MobileCameraProps = WebcamProps & {
  onScreenshot: (payload: MobileScreenshotPayload) => void;
  onBack: () => void;
  disabled: boolean;
  text?: ReactNode | string;
  title?: ReactNode | string;
};

const MobileWebcam = forwardRef<WebcamRefTypes, MobileCameraProps>(
  ({onScreenshot, onBack, title, text, disabled, className, ...props}, ref) => {
    const rootElement = getDocument()?.body;
    const {t} = useTranslation();

    const handleNativeScreenshot = useCallback(
      (photo?: Promise<string>) => onScreenshot({isNative: true, photo}),
      [onScreenshot],
    );

    return createPortal(
      <div className="mobile-camera flex justify-center">
        <button
          type="button"
          onClick={onBack}
          className="mobile-camera__back-btn fixed left-7 top-9 z-[7] flex border-0 bg-transparent p-0 landscape:left-[50px] landscape:top-[70px]"
        >
          <ChevronLeft size={32} strokeWidth={3} className="text-white" />
        </button>
        {title && (
          <div className="mobile-camera__title fixed left-1/2 top-8 z-[7] -translate-x-1/2 whitespace-nowrap text-[18px] font-bold text-white landscape:top-[5px]">
            {title}
          </div>
        )}
        {text ? (
          <p className="mobile-camera__subtitle fixed top-[76px] z-[7] mx-auto my-0 px-6 text-center text-white landscape:hidden [@media(min-height:838px)]:top-28">
            {text}
          </p>
        ) : (
          <p className="mobile-camera__helper fixed top-[88px] z-[7] mx-auto my-0 px-6 text-center text-xs text-white landscape:hidden [@media(min-height:700px)]:[font-size:inherit]">
            {t('place_document_inside_frame')}
          </p>
        )}
        <Webcam
          ref={ref}
          {...props}
          className={cn(
            className,
            webcamClasses,
            props.roundedScanArea ? roundedWebcamClasses : scanAreaClasses,
          )}
        />
        <div className="fixed bottom-5 z-[7] flex w-[calc(100%-32px)] flex-col justify-between gap-5 self-center landscape:inset-y-0 landscape:right-10 landscape:my-auto landscape:h-auto landscape:w-auto landscape:justify-normal landscape:[&_.mobile-webcam-device-camera]:hidden">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onScreenshot({isNative: false})}
            className="mobile-camera__take-btn relative flex h-[75px] w-[75px] items-center justify-center self-center rounded-full border-0 bg-white p-[10px] shadow-[0_0_10px_#ffffff] hover:opacity-80 disabled:cursor-default disabled:opacity-60 disabled:hover:opacity-60"
          >
            <Camera size={26} className="text-[var(--primary)]" />
          </button>
          <DeviceCamera
            onChange={handleNativeScreenshot}
            facingMode={getAndMapFacingMode(props.additionalConstraints)}
          />
        </div>
      </div>,
      rootElement,
    );
  },
);

MobileWebcam.displayName = 'MobileWebcam';

export {MobileWebcam};
